import type { WidgetPublicConfig } from './types';

/** Composer ceiling, shared with the auto-grow logic that toggles its scrollbar. */
export const COMPOSER_MAX_HEIGHT = 120;

function relativeLuminance(color: string): number | null {
  const match = /^#([0-9a-f]{6})$/i.exec(color || '');
  if (!match) return null;
  const value = match[1];
  const channels = [0, 2, 4].map(offset => parseInt(value.slice(offset, offset + 2), 16) / 255)
    .map(channel => channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4));
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
}

function readableOnWhite(...colors: string[]): string {
  for (const color of colors) {
    const luminance = relativeLuminance(color);
    if (luminance == null) continue;
    if (1.05 / (luminance + 0.05) >= 4.5) return color;
  }
  return '#334155';
}

/**
 * Admin-supplied colors reach the browser through the public config and are
 * interpolated straight into the stylesheet, so only literal color syntax is
 * accepted — anything else could close the declaration and inject rules.
 */
function safeCssColor(value: unknown): string | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  if (/^#([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(raw)) return raw;
  if (/^rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+\s*(,\s*[\d.]+\s*)?\)$/i.test(raw)) return raw;
  if (/^[a-z]{3,20}$/i.test(raw)) return raw;
  return null;
}

/** `rgba()` triplet for a hex color, so effects can fade a themed ring out. */
function rgbTriplet(color: string, fallback = '37, 99, 235'): string {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color || '');
  if (!match) return fallback;
  const hex = match[1].length === 3 ? match[1].replace(/./g, c => c + c) : match[1];
  return [0, 2, 4].map(offset => parseInt(hex.slice(offset, offset + 2), 16)).join(', ');
}

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
}

/**
 * White label text on a pale brand color reads as "disabled". Flip to near-black
 * whenever white wouldn't hold up against the button background.
 */
function readableTextOn(background: string): string {
  const luminance = relativeLuminance(background);
  if (luminance == null) return '#FFFFFF';
  return 1.05 / (luminance + 0.05) >= 3 ? '#FFFFFF' : '#0F172A';
}

// Inline stylesheet for the shadow-DOM mounted widget. Variables come
// from theme so the host config controls the palette without leaking
// CSS into the surrounding page.
export function buildStyles(cfg: WidgetPublicConfig, preview = false): string {
  const t: any = cfg.theme || {};
  // Accept both the public DTO names (secondaryColor, backgroundColor)
  // and the editor's persisted names (accentColor, bgColor).
  const primary = t.primaryColor || '#2563EB';
  const secondary = t.secondaryColor || t.accentColor || '#1E40AF';
  const voiceControl = readableOnWhite(secondary, primary);
  // Action buttons (form submit, send, launcher) default to the primary color
  // but can be themed independently.
  const button = t.buttonColor || primary;
  const buttonText = t.buttonTextColor || readableTextOn(button);
  const text = t.textColor || '#0F172A';
  const bg = t.backgroundColor || t.bgColor || '#FFFFFF';
  const bubble = t.bubbleColor || '#F1F5F9';
  const font = t.fontFamily || 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
  const radius = t.borderRadius || '16px';
  const position = t.position || 'bottom-right';
  const horizontal = position === 'bottom-left' ? 'left: 24px;' : 'right: 24px;';
  // Panel size preset — lets integrators opt into a roomier chat surface.
  const sizePresets: Record<string, { w: number; h: number }> = {
    standard: { w: 360, h: 560 },
    large: { w: 440, h: 680 },
    xlarge: { w: 520, h: 760 },
    fill: { w: 100000, h: 100000 },
  };
  const panel = sizePresets[String(t.size || 'standard')] || sizePresets.standard;
  const hb: any = (cfg.behavior as any)?.helpBubble || {};
  const hbBg = safeCssColor(hb.backgroundColor) || bg;
  const hbText = safeCssColor(hb.textColor) || text;
  const hbAccent = safeCssColor(hb.borderColor) || primary;
  const hbBorderWidth = clampNumber(hb.borderWidth, 0, 8, 1);
  const hbBorderColor = safeCssColor(hb.borderColor) || `color-mix(in srgb, ${text} 20%, ${bg})`;
  const hbRadius = clampNumber(hb.borderRadius, 0, 40, 8);
  const hbRing = rgbTriplet(hbAccent, rgbTriplet(primary));
  const hbImageRadius = hb.imageShape === 'square' ? '0' : hb.imageShape === 'rounded' ? '10px' : '999px';
  return `
    :host { all: initial; }
    *, *::before, *::after { box-sizing: border-box; }
    .root { position: fixed; bottom: 24px; ${horizontal} z-index: 2147483600; font-family: ${font}; color: ${text}; }
    .launcher {
      width: 60px; height: 60px; border-radius: 999px; border: 0; cursor: pointer;
      background: ${button}; color: ${buttonText}; box-shadow: 0 12px 24px rgba(15,23,42,0.18);
      display: flex; align-items: center; justify-content: center; font-size: 26px;
      transition: transform .15s ease; position: relative;
    }
    .launcher:hover { transform: translateY(-2px); }
    .help-bubble {
      position: absolute; bottom: 76px; ${position === 'bottom-left' ? 'left: 0;' : 'right: 0;'}
      width: 280px; max-width: calc(100vw - 48px); max-height: calc(100dvh - 124px); overflow: auto;
      display: grid; grid-template-columns: minmax(0, 1fr) 32px; align-items: start;
      border: ${hbBorderWidth}px solid ${hbBorderColor}; border-radius: ${hbRadius}px;
      background: ${hbBg}; color: ${hbText}; box-shadow: 0 6px 22px rgba(0,0,0,.16);
      animation: autoqmHelpAppear .2s ease-out;
    }
    .help-bubble.has-image { grid-template-columns: auto minmax(0, 1fr) 32px; }
    .help-bubble-image {
      width: 44px; height: 44px; object-fit: cover; border-radius: ${hbImageRadius};
      margin: 14px 0 14px 14px; flex-shrink: 0;
    }
    .help-bubble button { border: 0; background: transparent; color: inherit; font-family: inherit; cursor: pointer; letter-spacing: 0; }
    .help-bubble-message { padding: 14px; font-size: 14px; line-height: 1.5; text-align: left; white-space: pre-wrap; overflow-wrap: anywhere; min-width: 0; }
    .help-bubble-message.is-entering { animation: autoqmHelpPhrase .28s ease-out; }
    .help-bubble-dismiss { width: 32px; height: 32px; font-size: 22px; line-height: 1; }
    .help-bubble button:hover { background: color-mix(in srgb, ${hbText} 6%, ${hbBg}); }
    .help-bubble button:focus-visible { outline: 2px solid ${hbAccent}; outline-offset: -3px; }
    .help-bubble.fx-pulse { animation: autoqmHelpAppear .2s ease-out, autoqmHelpRing 1.9s ease-out .2s infinite; }
    .help-bubble.fx-glow { animation: autoqmHelpAppear .2s ease-out, autoqmHelpGlow 2.4s ease-in-out .2s infinite; }
    .help-bubble.fx-bounce { animation: autoqmHelpAppear .2s ease-out, autoqmHelpBounce 1.6s ease-in-out .2s infinite; }
    .help-bubble.fx-shake { animation: autoqmHelpAppear .2s ease-out, autoqmHelpShake 3s ease-in-out .2s infinite; }
    .help-bubble.fx-float { animation: autoqmHelpAppear .2s ease-out, autoqmHelpFloat 3.4s ease-in-out .2s infinite; }
    .launcher.attention { animation: autoqmLauncherRing 1.9s ease-out infinite; }
    @keyframes autoqmHelpAppear { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes autoqmHelpPhrase { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes autoqmHelpRing {
      0%   { box-shadow: 0 6px 22px rgba(0,0,0,.16), 0 0 0 0 rgba(${hbRing}, .55); }
      70%  { box-shadow: 0 6px 22px rgba(0,0,0,.16), 0 0 0 12px rgba(${hbRing}, 0); }
      100% { box-shadow: 0 6px 22px rgba(0,0,0,.16), 0 0 0 0 rgba(${hbRing}, 0); }
    }
    @keyframes autoqmHelpGlow {
      0%, 100% { box-shadow: 0 6px 22px rgba(0,0,0,.16), 0 0 6px 0 rgba(${hbRing}, .35); }
      50%      { box-shadow: 0 6px 26px rgba(0,0,0,.2), 0 0 22px 4px rgba(${hbRing}, .75); }
    }
    @keyframes autoqmHelpBounce {
      0%, 100% { transform: translateY(0); }
      50%      { transform: translateY(-7px); }
    }
    @keyframes autoqmHelpShake {
      0%, 82%, 100%   { transform: translateX(0) rotate(0deg); }
      85%, 93%        { transform: translateX(-4px) rotate(-1.2deg); }
      89%, 97%        { transform: translateX(4px) rotate(1.2deg); }
    }
    @keyframes autoqmHelpFloat {
      0%, 100% { transform: translateY(0) rotate(-.4deg); }
      50%      { transform: translateY(-5px) rotate(.4deg); }
    }
    @keyframes autoqmLauncherRing {
      0%   { box-shadow: 0 12px 24px rgba(15,23,42,0.18), 0 0 0 0 rgba(${hbRing}, .55); }
      70%  { box-shadow: 0 12px 24px rgba(15,23,42,0.18), 0 0 0 14px rgba(${hbRing}, 0); }
      100% { box-shadow: 0 12px 24px rgba(15,23,42,0.18), 0 0 0 0 rgba(${hbRing}, 0); }
    }
    @media (prefers-reduced-motion: reduce) {
      .help-bubble, .help-bubble.fx-pulse, .help-bubble.fx-glow, .help-bubble.fx-bounce,
      .help-bubble.fx-shake, .help-bubble.fx-float,
      .help-bubble-message.is-entering, .launcher.attention { animation: none; }
    }
    .launcher .active-dot {
      position: absolute; top: 4px; right: 4px; width: 12px; height: 12px;
      border-radius: 999px; background: #22C55E; border: 2px solid #fff;
      box-shadow: 0 0 0 0 rgba(34,197,94,0.6);
      animation: autoqmWidgetPulse 1.6s ease-out infinite;
    }
    @keyframes autoqmWidgetPulse {
      0%   { box-shadow: 0 0 0 0 rgba(34,197,94,0.55); }
      70%  { box-shadow: 0 0 0 8px rgba(34,197,94,0); }
      100% { box-shadow: 0 0 0 0 rgba(34,197,94,0); }
    }
    .panel {
      width: ${panel.w}px; max-width: calc(100vw - 32px); height: ${panel.h}px; max-height: calc(100vh - 64px);
      background: ${bg}; border-radius: ${radius}; box-shadow: 0 24px 48px rgba(15,23,42,0.22);
      display: flex; flex-direction: column; overflow: hidden;
    }
    .header {
      background: ${primary}; color: #fff; padding: 14px 16px;
      display: flex; align-items: center; gap: 10px;
    }
    .header .avatar { width: 36px; height: 36px; border-radius: 999px; background: rgba(255,255,255,0.18); display:flex; align-items:center; justify-content:center; font-weight:600; }
    .header .header-logo { display: block; width: auto; max-width: 45%; object-fit: contain; flex-shrink: 0; }
    .header .header-lockup { display: flex; align-items: center; gap: 10px; min-width: 0; flex: 1; }
    .header.header-align-center .header-lockup { justify-content: center; text-align: center; }
    .header.header-align-center .header-titles { align-items: center; }
    .header.header-align-right .header-lockup { justify-content: flex-end; text-align: right; }
    .header.header-align-right .header-titles { align-items: flex-end; }
    .header .header-titles { display: flex; flex-direction: column; min-width: 0; }
    .header .title-row { display: flex; align-items: center; gap: 6px; }
    .header .title { font-size: 15px; font-weight: 600; line-height: 1.2; }
    .header .status-dot {
      width: 8px; height: 8px; border-radius: 999px;
      background: rgba(255,255,255,0.35);
      flex-shrink: 0;
      transition: background-color .2s ease;
    }
    .header .status-dot.connected {
      background: #22C55E;
      box-shadow: 0 0 0 0 rgba(34,197,94,0.65);
      animation: autoqmWidgetPulse 1.8s ease-out infinite;
    }
    .header .subtitle { font-size: 12px; opacity: 0.85; }
    .header .spacer { flex: 1; }
    .header button { background: transparent; border: 0; color: #fff; cursor: pointer; font-size: 18px; opacity: 0.9; padding: 4px 6px; display: inline-flex; align-items: center; justify-content: center; }
    .header.header-custom-text button { color: inherit; }
    .header button:hover { opacity: 1; }
    .header .end-btn { font-size: 14px; }
    .header .end-btn svg { display: block; }
    .body { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 8px; background: ${bg}; }
    .voice-avatar-stage { position: relative; flex: 0 0 96px; min-height: 96px; overflow: hidden; border-bottom: 1px solid #E2E8F0; background: color-mix(in srgb, ${bg} 92%, ${primary} 8%); transition: flex-basis .2s ease, min-height .2s ease; }
    .voice-avatar-stage[hidden] { display: none; }
    .voice-avatar-stage.immersive { flex: 1 1 auto; min-height: 220px; }
    /* Expanded is an avatar-only stage: the transcript is hidden entirely so the
       widget matches the AI IVR tester's immersive voice view. */
    .panel-body.avatar-immersive .body { display: none; }
    .panel-body.avatar-immersive.loading .body { display: flex; flex: 0 0 auto; }
    .voice-avatar-canvas { display: block; width: 100%; height: 100%; }
    .voice-avatar-label { position: absolute; left: 12px; bottom: 8px; font-size: 11px; font-weight: 600; color: ${text}; opacity: .72; }
    .voice-avatar-layout { position: absolute; top: 8px; right: 8px; width: 28px; height: 28px; border: 0; border-radius: 6px; background: rgba(255,255,255,.82); color: ${text}; cursor: pointer; font-size: 15px; line-height: 1; }
    .voice-avatar-layout:hover { background: #fff; }
    @media (prefers-reduced-motion: reduce) { .voice-avatar-stage { transition: none; } }
    .msg { padding: 10px 12px; border-radius: 14px; max-width: 80%; font-size: 14px; line-height: 1.45; white-space: pre-wrap; word-wrap: break-word; }
    .msg.user { align-self: flex-end; background: ${primary}; color: #fff; border-bottom-right-radius: 4px; }
    .msg.agent { align-self: flex-start; background: ${bubble}; color: ${text}; border-bottom-left-radius: 4px; }
    .msg-row { display: flex; align-items: flex-end; gap: 6px; align-self: flex-start; max-width: 88%; }
    .msg-row .msg.agent { align-self: auto; max-width: 100%; }
    .msg-avatar { width: 26px; height: 26px; border-radius: 999px; flex: 0 0 auto; object-fit: cover; background: rgba(0,0,0,0.06); }
    .msg-avatar-initial { display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; color: #fff; background: ${primary}; }
    .msg.partial { opacity: 0.7; font-style: italic; }
    .msg.voice { padding: 6px 8px; }
    .msg-audio { display: flex; align-items: center; gap: 8px; margin-top: 6px; width: 220px; max-width: 100%; }
    .msg.voice .msg-audio { margin-top: 0; }
    .msg-audio audio { display: none; }
    .msg-audio-btn { flex: 0 0 auto; width: 34px; height: 34px; border-radius: 999px; border: 0; cursor: pointer; display: flex; align-items: center; justify-content: center; padding: 0; background: ${primary}; color: #fff; transition: filter 0.15s ease, transform 0.05s ease; }
    .msg-audio-btn:hover { filter: brightness(1.08); }
    .msg-audio-btn:active { transform: scale(0.94); }
    .msg-audio-btn svg { display: block; }
    .msg.agent .msg-audio-btn { background: ${primary}; }
    /* On the brand-coloured user bubble an identically-coloured button is
       invisible — invert it so the play/pause control stays legible. */
    .msg.user .msg-audio-btn { background: #fff; color: ${primary}; }
    .msg.user .msg-audio-btn:hover { filter: brightness(0.96); }
    .msg-audio-track { position: relative; flex: 1 1 auto; height: 3px; border-radius: 999px; background: rgba(0,0,0,0.18); cursor: pointer; touch-action: none; }
    .msg.user .msg-audio-track { background: rgba(255,255,255,0.35); }
    .msg-audio-fill { position: absolute; left: 0; top: 0; height: 100%; width: 0%; border-radius: 999px; background: ${primary}; }
    .msg.user .msg-audio-fill { background: #fff; }
    .msg-audio-knob { position: absolute; top: 50%; left: 0%; width: 11px; height: 11px; border-radius: 999px; background: ${primary}; transform: translate(-50%, -50%); box-shadow: 0 1px 2px rgba(0,0,0,0.25); }
    .msg.user .msg-audio-knob { background: #fff; }
    .msg-audio-time { flex: 0 0 auto; font-size: 11px; line-height: 1; opacity: 0.7; min-width: 30px; text-align: right; font-variant-numeric: tabular-nums; }
    .msg-time { margin-top: 4px; font-size: 10px; line-height: 1; opacity: 0.6; text-align: right; }
    .msg-link { color: inherit; text-decoration: underline; overflow-wrap: anywhere; }
    .msg.agent .msg-link { color: ${primary}; }
    .link-preview { width: 260px; max-width: 100%; margin: -4px -6px 8px; border-radius: 10px; overflow: hidden; background: rgba(0,0,0,0.05); border-left: 3px solid ${primary}; }
    .link-preview[hidden] { display: none; }
    .lp-media { position: relative; background: #0F172A; }
    .lp-media:empty { display: none; }
    .lp-media-video { aspect-ratio: 16 / 9; }
    .lp-image .lp-media, .lp-page .lp-media { background: transparent; }
    .lp-thumb { display: block; width: 100%; max-height: 180px; object-fit: cover; }
    .lp-media-video .lp-thumb, .lp-player, .lp-frame { position: absolute; inset: 0; width: 100%; height: 100%; max-height: none; border: 0; object-fit: cover; }
    .lp-player { object-fit: contain; background: #000; }
    .lp-poster { position: absolute; inset: 0; width: 100%; height: 100%; padding: 0; border: 0; cursor: pointer; background: #0F172A; display: flex; align-items: center; justify-content: center; }
    .lp-play { position: relative; z-index: 1; width: 48px; height: 48px; border-radius: 999px; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.6); color: #fff; transition: transform .12s ease, background .12s ease; }
    .lp-poster:hover .lp-play { transform: scale(1.06); background: ${primary}; }
    .lp-poster:focus-visible { outline: 2px solid ${primary}; outline-offset: -2px; }
    .lp-image-link { display: block; }
    .lp-meta { display: flex; flex-direction: column; gap: 2px; padding: 8px 10px; color: ${text}; text-decoration: none; }
    .lp-meta:hover .lp-title { text-decoration: underline; }
    .lp-site { font-size: 11px; opacity: 0.65; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .lp-title { font-size: 13px; font-weight: 600; line-height: 1.3; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
    .lp-desc { font-size: 12px; line-height: 1.35; opacity: 0.8; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
    .lp-title[hidden], .lp-desc[hidden] { display: none; }
    .msg.voice .link-preview { margin: 0 0 6px; }
    .msg.agent .msg-time, .msg-row .msg.agent .msg-time { text-align: left; }
    .msg.typing { display: flex; gap: 4px; align-items: center; padding: 12px 14px; }
    .msg.typing .typing-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; opacity: 0.4; animation: autoqmWidgetTyping 1.2s infinite ease-in-out; }
    .msg.typing .typing-dot:nth-child(2) { animation-delay: 0.2s; }
    .msg.typing .typing-dot:nth-child(3) { animation-delay: 0.4s; }
    @keyframes autoqmWidgetTyping { 0%, 60%, 100% { opacity: 0.25; transform: translateY(0); } 30% { opacity: 0.9; transform: translateY(-3px); } }
    .rich-content { align-self: stretch; max-width: 100%; background: ${bubble}; border: 1px solid #E2E8F0; border-radius: 14px; padding: 12px; }
    .rich-content-frame { display: block; width: 100%; border: 0; }
    .rich-content-error, .rich-content-text { font-size: 13px; color: ${text}; }
    .ac-card { display: flex; flex-direction: column; gap: 8px; color: ${text}; }
    .ac-text { font-size: 14px; line-height: 1.4; }
    .ac-image { border-radius: 8px; }
    .ac-imageset { display: flex; flex-wrap: wrap; gap: 6px; }
    .ac-factset { display: flex; flex-direction: column; gap: 2px; }
    .ac-fact { display: flex; gap: 8px; font-size: 13px; }
    .ac-fact-title { font-weight: 600; min-width: 80px; }
    .ac-container { display: flex; flex-direction: column; gap: 6px; }
    .ac-field { display: flex; flex-direction: column; gap: 4px; }
    .ac-label { font-size: 12px; font-weight: 600; color: #475569; }
    .ac-input { border: 1px solid #CBD5E1; border-radius: 8px; padding: 7px 9px; font-size: 14px; font-family: inherit; width: 100%; box-sizing: border-box; }
    .ac-toggle { display: flex; align-items: center; gap: 6px; font-size: 13px; }
    .ac-choices { display: flex; flex-direction: column; gap: 4px; }
    .ac-choice { display: flex; align-items: center; gap: 6px; font-size: 13px; }
    .ac-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
    .ac-btn { border: 0; border-radius: 10px; padding: 8px 14px; font-size: 14px; font-weight: 600; cursor: pointer; text-decoration: none; }
    .ac-btn-primary { background: ${primary}; color: #fff; }
    .ac-btn-primary:disabled { opacity: 0.6; cursor: default; }
    .ac-btn-link { background: #F1F5F9; color: ${text}; display: inline-block; }
    .status { font-size: 12px; color: #64748B; text-align: center; padding: 4px 0; }
    .footer { border-top: 1px solid #E2E8F0; padding: 10px 12px; background: ${bg}; }
    .input-row { position: relative; display: flex; gap: 8px; align-items: flex-end; }
    .input-row textarea {
      flex: 1; resize: none; border: 1px solid #CBD5E1; border-radius: 20px; padding: 9px 14px;
      font-family: inherit; font-size: 14px; min-height: 40px; max-height: ${COMPOSER_MAX_HEIGHT}px; overflow-y: hidden; outline: none; color: ${text}; background: #fff;
    }
    .input-row textarea:focus { border-color: ${primary}; box-shadow: 0 0 0 3px rgba(37,99,235,0.15); }
    .input-row button {
      flex: 0 0 auto; position: relative; z-index: 2;
      background: ${primary}; color: #fff; border: 0; border-radius: 999px;
      width: 40px; height: 40px; cursor: pointer; font-size: 18px;
      display: inline-flex; align-items: center; justify-content: center;
    }
    .input-row button:disabled { background: #94A3B8; cursor: not-allowed; }
    /* WhatsApp-style "+" attach button on the LEFT of the field. */
    .input-row .attach-btn { background: transparent; color: #64748B; font-size: 26px; font-weight: 400; line-height: 1; }
    .input-row .attach-btn:hover { background: #F1F5F9; color: ${text}; }
    .input-row .send-btn { background: ${button}; color: ${buttonText}; }
    /* Morph: the in-row mic shows while the field is empty; send appears on type. */
    .input-row.has-voice-control .send-btn { display: none; }
    .input-row.has-voice-control.has-text .send-btn { display: inline-flex; }
    /* WhatsApp-style recording overlay rendered OVER the text bar. */
    .rec-overlay {
      position: absolute; left: 0; top: 0; bottom: 0; right: 0; z-index: 3;
      display: none; align-items: center; gap: 10px; padding: 0 14px;
      background: ${bg}; border: 1px solid #CBD5E1; border-radius: 20px;
    }
    .input-row.recording .rec-overlay { display: flex; }
    .input-row.recording .send-btn { display: none; }
    .rec-dot { width: 10px; height: 10px; border-radius: 50%; background: #DC2626; animation: autoqm-rec-pulse 1.2s ease-in-out infinite; }
    .rec-label { font-size: 13px; color: ${text}; }
    .rec-time { font-size: 13px; color: #64748B; margin-left: auto; font-variant-numeric: tabular-nums; }
    .files-row { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
    .file-chip { display: inline-flex; align-items: center; gap: 4px; padding: 4px 6px 4px 10px; border-radius: 999px; font-size: 11px; max-width: 100%; }
    .file-chip-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .file-chip-remove { flex: 0 0 auto; width: 16px; height: 16px; border: 0; border-radius: 999px; cursor: pointer; padding: 0; font-size: 13px; line-height: 1; display: inline-flex; align-items: center; justify-content: center; background: rgba(0,0,0,0.12); color: inherit; }
    .file-chip-remove:hover { background: rgba(0,0,0,0.24); }
    .file-chip.uploading { background: #FEF3C7; color: #92400E; }
    .file-chip.ready { background: #DCFCE7; color: #166534; }
    .file-chip.failed { background: #FEE2E2; color: #991B1B; }
    .msg-files { display: flex; flex-direction: column; gap: 4px; margin-top: 6px; }
    .msg-file { display: inline-flex; align-items: center; gap: 6px; padding: 6px 8px; border-radius: 8px; font-size: 12px; background: rgba(255,255,255,0.18); max-width: 100%; }
    .msg.user .msg-file { background: rgba(255,255,255,0.2); }
    .msg.agent .msg-file { background: rgba(0,0,0,0.05); }
    .msg-file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .msg-feedback { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; margin: 4px 0 2px 0; font-size: 12px; color: #64748B; }
    .msg-feedback-btn { border: 0; background: transparent; cursor: pointer; padding: 2px 4px; border-radius: 6px; font-size: 14px; line-height: 1; opacity: 0.65; }
    .msg-feedback-btn:hover { opacity: 1; background: rgba(0,0,0,0.06); }
    .msg-feedback-btn.selected { opacity: 1; }
    .msg-feedback-form { display: flex; flex-direction: column; gap: 6px; margin: 2px 0 6px 0; }
    .msg-feedback-form label { font-size: 12px; color: #64748B; }
    .msg-feedback-form textarea { width: 100%; box-sizing: border-box; min-height: 68px; resize: vertical; padding: 8px; border: 1px solid #CBD5E1; border-radius: 8px; font: inherit; font-size: 13px; color: ${text}; background: #FFFFFF; }
    .msg-feedback-form textarea:focus { outline: 2px solid ${primary}; outline-offset: -1px; }
    .msg-feedback-send { align-self: flex-start; border: 1px solid #CBD5E1; background: #FFFFFF; color: ${text}; border-radius: 6px; padding: 4px 10px; font-size: 12px; cursor: pointer; }
    .msg-feedback-send:hover { background: #F1F5F9; }
    .msg-feedback-thanks { font-size: 12px; color: #64748B; margin: 4px 0 2px 0; }
    .pill-row { display: flex; flex-wrap: wrap; gap: 8px; margin: 6px 0 4px 0; }
    .pill { display: inline-flex; align-items: center; gap: 6px; border-radius: 999px; padding: 8px 14px; font-size: 13px; font-weight: 500; cursor: pointer; border: 1px solid ${primary}; background: ${primary}; color: ${readableTextOn(primary)}; }
    .pill:hover { filter: brightness(0.94); }
    .pill.outline { background: transparent; color: ${readableOnWhite(primary, secondary)}; }
    .pill.outline:hover { background: rgba(0,0,0,0.04); }
    .pill[disabled] { opacity: 0.5; cursor: default; }
    .pill-form { display: flex; flex-direction: column; gap: 8px; margin: 6px 0; padding: 10px; border: 1px solid #E2E8F0; border-radius: 10px; background: #F8FAFC; }
    .pill-form label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #475569; }
    .pill-form input, .pill-form select { padding: 7px 8px; border: 1px solid #CBD5E1; border-radius: 8px; font: inherit; font-size: 13px; color: ${text}; background: #FFFFFF; }
    .pill-form-actions { display: flex; gap: 8px; }
    .precall { padding: 18px; display: flex; flex-direction: column; gap: 12px; overflow-y: auto; }
    .precall h3 { margin: 0 0 4px 0; font-size: 16px; color: ${text}; }
    .precall .form-description { margin: 0; font-size: 12px; color: #64748B; line-height: 1.45; }
    .precall label { display: flex; flex-direction: column; font-size: 12px; color: #475569; gap: 4px; }
    .precall .field { display: flex; flex-direction: column; font-size: 12px; color: #475569; gap: 4px; }
    .precall .field-wrap { display: flex; flex-direction: column; gap: 2px; }
    .precall .field-help { font-size: 11px; color: #94A3B8; line-height: 1.35; }
    .precall .field-check { flex-direction: row; align-items: flex-start; gap: 8px; }
    .precall .field-check input { width: 16px; height: 16px; flex: 0 0 auto; margin-top: 1px; }
    .precall .field-transcript { gap: 8px; }
    .precall .field-transcript input[type=email][hidden] { display: none; }
    .precall .field-label { font-size: 12px; color: #475569; }
    .precall .rating-row { display: flex; gap: 4px; }
    .precall .rating-star {
      background: transparent; border: 0; cursor: pointer; padding: 0 2px;
      font-size: 24px; line-height: 1; color: #CBD5E1; transition: color .12s ease;
    }
    .precall .rating-star.on { color: #F59E0B; }
    .precall .choice-row { display: flex; flex-wrap: wrap; gap: 4px; }
    .precall .choice-btn {
      min-width: 32px; padding: 4px 8px; border: 1px solid #CBD5E1; border-radius: 6px;
      background: #fff; color: #334155; font-size: 12px; cursor: pointer;
    }
    .precall .choice-row-nps .choice-btn { flex: 1 0 22px; min-width: 22px; padding: 4px 0; }
    .precall .choice-btn.on { background: ${primary}; border-color: ${primary}; color: #fff; }
    .precall .choice-scale { display: flex; justify-content: space-between; font-size: 10px; color: #94A3B8; }
    .precall .slider-row { display: flex; align-items: center; gap: 8px; }
    .precall .slider-row input[type=range] { flex: 1; padding: 0; border: 0; accent-color: ${primary}; background: transparent; }
    .precall .slider-bound { font-size: 11px; color: #94A3B8; font-variant-numeric: tabular-nums; }
    .precall .slider-value {
      min-width: 28px; text-align: center; font-size: 12px; font-weight: 600;
      color: ${primary}; font-variant-numeric: tabular-nums;
    }
    .precall input, .precall select, .precall textarea {
      border: 1px solid #CBD5E1; border-radius: 8px; padding: 8px 10px; font-family: inherit; font-size: 14px; color: ${text}; background: #fff;
    }
    .precall .submit {
      background: ${button}; color: ${buttonText}; border: 0; border-radius: 10px; padding: 10px 14px; cursor: pointer; font-weight: 600;
      transition: background-color .15s ease, color .15s ease;
    }
    .precall .submit:disabled { background: #E2E8F0; color: #94A3B8; cursor: not-allowed; }
    .precall .consent-actions { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 4px; }
    .precall .submit.secondary { background: transparent; color: ${text}; border: 1px solid #CBD5E1; }
    .precall .submit.secondary:hover { background: #F8FAFC; }
    .precall .skip {
      background: transparent; color: #64748B; border: 1px solid #CBD5E1;
      border-radius: 10px; padding: 8px 14px; cursor: pointer; font-size: 13px;
    }
    .precall .skip:hover { background: #F1F5F9; }
    .thank-you {
      flex: 1; display: flex; align-items: center; justify-content: center;
      padding: 24px; text-align: center; font-size: 15px; color: ${text};
      background: ${bg};
    }
    .modality-toggle { display: flex; gap: 6px; padding: 0 16px 8px 16px; }
    .modality-toggle button {
      flex: 1; padding: 6px 8px; border-radius: 999px; border: 1px solid #CBD5E1; background: #fff; cursor: pointer; font-size: 12px; color: ${text};
    }
    .modality-toggle button.active { background: ${secondary}; color: #fff; border-color: ${secondary}; }
    .voice-control { display: flex; width: 100%; gap: 8px; margin-top: 8px; align-items: center; }
    .mic-meter { display: flex; align-items: center; gap: 2px; flex: 0 0 auto; height: 14px; margin-left: 2px; }
    .mic-meter-seg { width: 3px; height: 100%; border-radius: 2px; background: rgba(255,255,255,0.28); transition: background-color .08s linear; }
    .mic-meter-seg.on { background: #ffffff; }
    .voice-control .mic-btn { flex: 1 1 auto; min-width: 0; min-height: 40px; border: 0; border-radius: 8px; padding: 8px 12px; display: flex; align-items: center; justify-content: center; gap: 8px; color: #fff; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
    .mic-btn { background: ${voiceControl}; touch-action: none; user-select: none; }
    .mic-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .mic-btn.recording { background: #DC2626; animation: autoqm-rec-pulse 1.2s ease-in-out infinite; }
    .mic-btn.muted { background: #94A3B8; }
    @keyframes autoqm-rec-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.5); } 50% { box-shadow: 0 0 0 6px rgba(220,38,38,0); } }
    /* Continuous open-mic: agent is verbalizing — the button itself is the
       interrupt affordance, so it goes red rather than rendering a stop icon. */
    .mic-btn.agent-speaking { background: #DC2626; animation: autoqm-agent-pulse 1.4s ease-in-out infinite; }
    @keyframes autoqm-agent-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(220,38,38,0.45); } 50% { box-shadow: 0 0 0 6px rgba(220,38,38,0); } }
    /* Icon-only ■ STOP button shown beside the agent bubble while it is speaking. */
    .agent-stop-btn { flex: 0 0 40px; align-self: stretch; width: 40px; min-height: 40px; border-radius: 8px; border: 0; cursor: pointer; background: #DC2626; color: #fff; display: inline-flex; align-items: center; justify-content: center; padding: 0; animation: autoqm-rec-pulse 1.2s ease-in-out infinite; }
    .agent-stop-btn:hover { background: #B91C1C; }
    /* Connecting/loading panel shown while the session/tools/knowledge spin up. */
    .widget-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 28px 16px; color: #64748B; text-align: center; }
    .widget-loading-spinner { width: 28px; height: 28px; border-radius: 50%; border: 3px solid #E2E8F0; border-top-color: ${secondary}; animation: autoqm-spin 0.8s linear infinite; }
    .widget-loading-text { font-size: 13px; color: #64748B; max-width: 240px; }
    @keyframes autoqm-spin { to { transform: rotate(360deg); } }
    .error { color: #B91C1C; font-size: 12px; padding: 6px 12px; text-align: center; }
    .end-conversation-btn {
      display: flex; align-items: center; justify-content: center; gap: 6px;
      width: 100%; margin-top: 8px; padding: 9px 12px; cursor: pointer;
      border: 1px solid #FCA5A5; border-radius: 10px; background: #FEF2F2;
      color: #B91C1C; font-family: inherit; font-size: 13px; font-weight: 600;
    }
    .end-conversation-btn:hover { background: #FEE2E2; }
    .end-conversation-btn svg { display: block; }
    .ai-disclaimer { font-size: 10px; line-height: 1.35; color: #94A3B8; text-align: center; padding: 8px 10px 0; }
    .powered { font-size: 10px; color: #94A3B8; text-align: center; padding: 4px 0; }
    .powered a { color: inherit; text-decoration: none; }

    /* Phone-sized viewports: the floating card becomes a full-screen sheet. */
    @media (max-width: 640px) {
      .root {
        bottom: calc(16px + env(safe-area-inset-bottom, 0px));
        ${position === 'bottom-left' ? 'left: 16px;' : 'right: 16px;'}
      }
      .launcher { width: 56px; height: 56px; font-size: 24px; }
      ${preview ? '' : `.panel {
        position: fixed; inset: 0;
        width: 100%; height: 100vh; height: 100dvh;
        max-width: none; max-height: none;
        border-radius: 0; box-shadow: none;
      }`}
      .header { padding-top: calc(14px + env(safe-area-inset-top, 0px)); }
      .footer { padding-bottom: calc(10px + env(safe-area-inset-bottom, 0px)); }
      .body { padding: 14px 12px; }
      .msg { max-width: 88%; font-size: 15px; }
      .msg-row { max-width: 94%; }
      /* iOS Safari zooms the page when focusing a field under 16px. */
      .input-row textarea, .ac-input { font-size: 16px; }
    }
  `;
}
